<template>
  <div class="container body-content">
    <EventTypePickerModal
      ref="PickerModal"
      :event-types="eventTypes"
      @add-selected-events="includeSelectedEvents"
    />
    <h1>Event Log</h1>

    <div class="filters-title-container">
      <h2>Filters</h2>
      <img src="../../../assets/event-log/filter-icon-green.png" alt="" />
    </div>

    <div>
      <div class="date-and-time">
        <div class="date-time-select-from">
          <label for="date-from">From: </label>
          <JyreDatePicker v-model="dateFrom" />
          <input type="time" class="form-control" id="date-from" v-model="timeFrom" />
        </div>

        <div class="date-time-select-to">
          <label for="date-to">To: </label>
          <JyreDatePicker v-model="dateTo" />
          <input type="time" class="form-control" id="date-to" v-model="timeTo" />
        </div>
      </div>
      <div class="apply-button-wrapper">
        <button class="btn btn-primary apply-btn" @click="getEvents" type="button">Apply</button>
      </div>

      <hr />
      <div class="event-types-container">
        <div>
          <h5>Event type:</h5>
          <button type="button" class="btn btn-default" @click="openModal">Select...</button>
        </div>
        <div class="selected-event-types">
          <ul>
            <li v-for="(eventType, index) in selectedEventTypes" :key="index">
              <EventTypeBadge
                :event-type-name="eventType"
                @remove-event-type="removeSelectedEventType(index)"
              />
            </li>
          </ul>
        </div>
      </div>
      <div class="clear-all-container">
        <a @click="clearSelectedEventTypes" @keydown.enter="clearSelectedEventTypes" tabindex="0"
          >Clear all</a
        >
      </div>

      <hr />

      <div class="group-button-and-search-container">
        <div class="dt-buttons btn-group">
          <button
            @click="copyEventLog"
            class="btn btn-default buttons-copy buttons-html5"
            tabindex="0"
            aria-controls="logTable"
            href="#"
            type="button"
          >
            <span>Copy</span>
          </button>
          <button
            @click="downloadCsvEventLog"
            class="btn btn-default buttons-copy buttons-html5"
            tabindex="0"
            aria-controls="logTable"
            href="#"
            type="button"
          >
            <span>CSV</span>
          </button>
        </div>
        <div class="search-container input-group">
          <label for="search" class="input-group-addon">Search:</label>
          <input type="text" id="search" v-model="searchInput" class="form-control" />
        </div>
      </div>
    </div>

    <EventLogTable v-if="!eventsAreLoading" :events="searchFilteredEvents" />
    <LoadingSpinner v-else />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import moment from "dayjs";
import axios from "axios";
import { debug as d } from "debug";
import { EventLogEntryApiModel } from "../../../api-models";
import EventLogTable from "../../../components/event-log/event-log-table.vue";
import EventTypePickerModal from "../../../components/event-log/event-type-picker-modal.vue";
import LoadingSpinner from "../../../components/loading-spinner.vue";
import { settleAll } from "../../../services/utilities/settle-all";
import JyreDatePicker from "../../../components/event-log/jyre-date-picker.vue";
import EventTypeBadge from "../../../components/event-log/event-type-badge.vue";

const debug = d("cs.EventLog");

const events = ref<EventLogEntryApiModel[]>([]);
const eventTypes = ref<string[]>([]);
const dateFrom = ref(moment().format("YYYY-MM-DD"));
const timeFrom = ref("00:00");
const dateTo = ref(moment().format("YYYY-MM-DD"));
const timeTo = ref(moment().format("HH:mm"));
const selectedEventTypes = ref<string[]>([]);
const searchInput = ref("");
const eventsAreLoading = ref(false);

// template refs
const PickerModal = ref<InstanceType<typeof EventTypePickerModal> | null>(null);

async function getEvents(): Promise<void> {
  eventsAreLoading.value = true;
  // Convert inputted dates/times to UTC before sending to the API
  const fromDateTimeUtc = moment(`${dateFrom.value} ${timeFrom.value}`, "YYYY-MM-DD HH:mm")
    .utc()
    .format("YYYY-MM-DD HH:mm");
  const toDateTimeUtc = moment(`${dateTo.value} ${timeTo.value}`, "YYYY-MM-DD HH:mm")
    .utc()
    .format("YYYY-MM-DD HH:mm");

  try {
    const res = await axios.get(
      `/api/admin/events?from=${fromDateTimeUtc}&to=${toDateTimeUtc}&includeTypes=${selectedEventTypes.value}`,
    );
    if (res.status === 200) {
      events.value = res.data.entries;
      eventsAreLoading.value = false;
    }
  } catch (e) {
    debug("/api/admin/events", e);
  }
}

async function getEventTypes(): Promise<void> {
  try {
    const res = await axios.get("/api/admin/eventtypes");
    if (res.status === 200) {
      eventTypes.value = res.data;
    }
  } catch (e) {
    debug("/api/admin/eventtypes", e);
  }
}

function clearSelectedEventTypes(): void {
  if (PickerModal.value === null) return;

  PickerModal.value.clearSelection();
  selectedEventTypes.value = [];
  getEvents();
}

function removeSelectedEventType(index: number): void {
  selectedEventTypes.value.splice(index, 1);
  getEvents();
}

function openModal(): void {
  if (PickerModal.value === null) return;
  PickerModal.value.openModal();
}

function includeSelectedEvents(selectedEvents: string[]): void {
  selectedEventTypes.value = selectedEvents;
  getEvents();
}

const searchFilteredEvents = computed(() => {
  if (searchInput.value === "") return events.value;
  return events.value.filter((event) =>
    JSON.stringify(event).toLowerCase().includes(searchInput.value),
  );
});

function getFormattedDate(timestamp: string): string {
  return moment(timestamp).format("D/M/YYYY H:mm:ss");
}

function getFormattedEvents(delimiter: string): string {
  const headers = [
    "Time",
    "Type",
    "Display Name",
    "Extra Properties",
    "Triggering user",
    "Users affected",
    "Groups affected",
  ];

  const formattedEvents = events.value.map((event) => {
    return [
      getFormattedDate(event.timestamp),
      event.type,
      event.displayName,
      event.extraProperties.length > 0
        ? event.extraProperties.map((p) => `${p.key}: ${p.value}`).join("; ")
        : "",
      event.triggeredByUser ? event.triggeredByUser.userEmail : "",
      event.usersAffected.length > 0 ? event.usersAffected.map((u) => u.userEmail).join("; ") : "",
      event.groupsAffected.length > 0
        ? event.groupsAffected.map((g) => g.groupName).join("; ")
        : "",
    ];
  });

  const tabSeperatedEvents = formattedEvents.map((event) => event.join(delimiter));
  tabSeperatedEvents.unshift(headers.join(delimiter));

  return tabSeperatedEvents.join("\r\n");
}

function copyEventLog(): void {
  const formattedEvents = getFormattedEvents("\t");
  window.navigator.clipboard.writeText(formattedEvents).then(() => {
    // eslint-disable-next-line no-alert
    alert(`Copied ${events.value.length} rows to clipboard`);
  });
}

function downloadCsvEventLog(): void {
  const csvContent = `data:text/csv;charset=utf-8,${getFormattedEvents(",")}`;
  const encodedUri = encodeURI(csvContent);

  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "Event Log - Jyre.csv");
  document.body.appendChild(link); // Required for FF
  link.click(); // This will download the data file
}

onBeforeMount(async () => {
  await settleAll([getEventTypes(), getEvents()]);
});
</script>

<style scoped lang="less">
@import (reference) "../../../styles/bootstrap/variables.less";

@media (max-width: @screen-lg-desktop) {
  .date-and-time {
    display: flex;
  }

  .date-time-select-from {
    display: flex;

    .form-control {
      width: 100px;
      display: inline-block;
    }
  }

  .date-time-select-to {
    .form-control {
      width: 100px;
      display: inline-block;
    }
  }

  .apply-button-wrapper {
    margin: 2rem 0;
    text-align: center;
  }

  .group-button-and-search-container .form-control {
    padding: 0;
  }

  label.input-group-addon {
    padding: 5px;
  }

  .apply-button-wrapper {
    padding: 0;
  }
}

.date-and-time {
  margin-top: 10px;

  label {
    margin-right: 10px;
  }

  input {
    margin-left: 10px;
  }

  & > div {
    display: inline-block;
    margin-right: 20px;
  }

  .form-control {
    display: inline-block;
    width: auto;
  }
}

hr {
  margin: 20px 0;
  border-top: 2px solid @practical-color;
  opacity: 0.5;
}

.filters-title-container {
  h2 {
    display: inline-block;
  }

  img {
    width: 20px;
    margin: 0 10px 12px;
  }
}

.event-types-container {
  display: flex;
}

.selected-event-types {
  position: relative;
  margin-left: 5rem;
  border-left: 2px solid #eee;
  height: 10rem;
  width: 100%;
  overflow-y: scroll;

  ul {
    list-style: none;
  }

  li {
    display: inline-block;
    margin: 1rem;
  }
}

.clear-all-container {
  display: flex;
  justify-content: flex-end;

  a {
    cursor: pointer;
  }
}

.group-button-and-search-container {
  margin: 3rem 0;
  display: flex;
  justify-content: space-between;
}
</style>
